import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { mapModes, singleSectionModes } from '../constants/map';
import { MapContext } from './MapContext';

interface RightMenuTabContextType {
  activeTab: string;
  popOutTabOpen: boolean;
  popOutTabPosition: { x: number; y: number };
  resetPopOutTab: () => void;
  setActiveTab: (activeTab: string) => void;
  setPopOutTabOpen: (popOutTabOpen: boolean) => void;
  setPopOutTabPosition: (popOutTabPosition: { x: number; y: number }) => void;
}

export const rightMenuTabs = {
  DESIGN: 'Design',
  INSPECT: 'Inspect',
};

// x: 0, y: 0 is the bottom-left corner of the right menu tab buttons
const DEFAULT_POSITION = {
  x: -306, // Shift left: width of side panel + 50px (to offset from pattern editor)
  y: -33, // Shift up: height of right menu tab buttons - 10px
};

const initState = {
  activeTab: '',
  popOutTabOpen: false,
  popOutTabPosition: DEFAULT_POSITION,
  resetPopOutTab: () => {},
  setActiveTab: () => {},
  setPopOutTabOpen: () => {},
  setPopOutTabPosition: () => {},
};

const ROW_SECTION_MAP_MODES = [mapModes.ROW_ENGAGED, ...singleSectionModes];

export const RightMenuTabContext = createContext<RightMenuTabContextType>(
  initState,
);

export const RightMenuTabContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    // @ts-expect-error MapContext not yet migrated to TS
    mapState: { mapMode },
  } = useContext(MapContext);
  const [activeTab, setActiveTab] = useState(rightMenuTabs.DESIGN);
  const [popOutTabOpen, setPopOutTabOpen] = useState(false);
  const [popOutTabPosition, setPopOutTabPosition] = useState(DEFAULT_POSITION);

  const resetPopOutTab = useCallback(() => {
    setPopOutTabOpen(false);
    setPopOutTabPosition(DEFAULT_POSITION);
  }, [setPopOutTabOpen, setPopOutTabPosition]);

  // Set activeTab to DESIGN if opening the pop out
  useEffect(() => {
    if (popOutTabOpen) setActiveTab(rightMenuTabs.DESIGN);
  }, [popOutTabOpen, setActiveTab]);

  // Close pop out if leaving row or section mode
  useEffect(() => {
    if (!ROW_SECTION_MAP_MODES.includes(mapMode)) resetPopOutTab();
  }, [mapMode, resetPopOutTab]);

  const value = useMemo(
    () => ({
      activeTab,
      popOutTabOpen,
      popOutTabPosition,
      resetPopOutTab,
      setActiveTab,
      setPopOutTabOpen,
      setPopOutTabPosition,
    }),
    [
      activeTab,
      popOutTabOpen,
      popOutTabPosition,
      resetPopOutTab,
      setActiveTab,
      setPopOutTabOpen,
      setPopOutTabPosition,
    ],
  );

  return (
    <RightMenuTabContext.Provider value={value}>
      {children}
    </RightMenuTabContext.Provider>
  );
};

export const useRightMenuTabContext = () => useContext(RightMenuTabContext);
